import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import { ScrollArea } from '@deepguide-ai/ui/scroll-area'
import { cn } from '@/lib/utils'
import type { ActionPillSuggestionItem } from './action-pill-suggestion'

interface ProviderGroup {
  provider: string
  providerLabel: string
  connected: boolean
  actions: ActionPillSuggestionItem[]
}

// Provider → category for the picker grid. One-level grouping so users
// can skim by category instead of scanning a flat list of ~30
// integrations. Anything not listed falls into "Other".
//
// Keep keys lowercase to match the provider strings emitted by the
// suggestion source. Aim for short, scannable labels — order of the
// CATEGORY_ORDER array drives the visual sequence.
const PROVIDER_CATEGORY: Record<string, string> = {
  // Cloud
  aws: 'Cloud',
  gcp: 'Cloud',
  azure: 'Cloud',
  m365: 'Cloud',
  kubernetes: 'Cloud',
  // Identity
  okta: 'Identity & Access',
  jumpcloud: 'Identity & Access',
  onelogin: 'Identity & Access',
  google_workspace: 'Identity & Access',
  auth0: 'Identity & Access',
  // Code
  github: 'Code',
  gitlab: 'Code',
  bitbucket: 'Code',
  // Endpoint / MDM
  intune: 'Endpoint & Devices',
  jamf: 'Endpoint & Devices',
  jamfpro: 'Endpoint & Devices',
  fleetdm: 'Endpoint & Devices',
  crowdstrike: 'Endpoint & Devices',
  // Observability / Security
  datadog: 'Observability & Security',
  snyk: 'Observability & Security',
  // Communications
  slack: 'Communications',
  microsoftteams: 'Communications',
  zoom: 'Communications',
  // Knowledge
  notion: 'Knowledge',
  confluence: 'Knowledge',
  gitbook: 'Knowledge',
  // Ticketing & Support
  jira: 'Ticketing & Support',
  linear: 'Ticketing & Support',
  zendesk: 'Ticketing & Support',
  intercom: 'Ticketing & Support',
  freshdesk: 'Ticketing & Support',
  // HR & People
  rippling: 'HR & People',
  bamboohr: 'HR & People',
  deel: 'HR & People',
  gusto: 'HR & People',
  workday: 'HR & People',
}

const CATEGORY_ORDER = [
  'Cloud',
  'Identity & Access',
  'Code',
  'Endpoint & Devices',
  'Observability & Security',
  'Communications',
  'Knowledge',
  'Ticketing & Support',
  'HR & People',
  'Other',
]

function categoryFor(provider: string): string {
  return PROVIDER_CATEGORY[provider.toLowerCase()] ?? 'Other'
}

function groupProvidersByCategory(
  groups: ProviderGroup[],
): Array<{ category: string; groups: ProviderGroup[] }> {
  const buckets = new Map<string, ProviderGroup[]>()
  for (const group of groups) {
    const cat = categoryFor(group.provider)
    if (!buckets.has(cat)) buckets.set(cat, [])
    buckets.get(cat)!.push(group)
  }
  return CATEGORY_ORDER.filter((cat) => buckets.has(cat)).map((cat) => ({
    category: cat,
    groups: buckets.get(cat)!,
  }))
}

interface ActionPillMenuListProps {
  items: ProviderGroup[]
  /** TipTap forwards the live query string from the suggestion plugin. */
  query?: string
  command: (item: ActionPillSuggestionItem) => void
}

type View = 'providers' | 'actions'

export const ActionPillMenuList = React.forwardRef(
  (props: ActionPillMenuListProps, ref) => {
    const scrollRef = useRef<HTMLDivElement>(null)
    const [selectedIndex, setSelectedIndex] = useState(0)
    const [view, setView] = useState<View>('providers')
    // Track both the provider id and the human label so the drill-down
    // header can stay rendered even when the current query filters every
    // action out of that provider (the group itself disappears from the
    // server response in that case).
    const [selectedProvider, setSelectedProvider] = useState<
      { id: string; label: string } | null
    >(null)

    const query = (props.query ?? '').trim()
    const isSearching = query.length > 0

    const providers = props.items
    const selectedProviderActions = useMemo(() => {
      if (!selectedProvider) return []
      return (
        providers.find((group) => group.provider === selectedProvider.id)
          ?.actions ?? []
      )
    }, [providers, selectedProvider])

    // Drill-down is sticky once the user picks a provider: typing then
    // filters within that provider. To search across all providers,
    // navigate back to the grid first.
    //
    //   provider grid + no query  → grid
    //   provider grid + query     → flat search across all providers
    //   provider picked           → that provider's actions (filtered by
    //                               query if any; can be empty)
    const flatActions = useMemo(() => {
      if (view === 'actions' && selectedProvider) {
        return selectedProviderActions
      }
      if (isSearching) {
        return providers.flatMap((group) => group.actions)
      }
      return []
    }, [isSearching, view, selectedProvider, selectedProviderActions, providers])

    // Pre-compute the category buckets once per `providers` change.
    // Each bucket carries its starting offset in the flat keyboard-nav
    // list so the JSX doesn't need a render-time mutating counter.
    const categorized = useMemo(() => {
      const buckets = groupProvidersByCategory(providers)
      let offset = 0
      return buckets.map((bucket) => {
        const startIndex = offset
        offset += bucket.groups.length
        return { ...bucket, startIndex }
      })
    }, [providers])

    // Reset selection whenever the visible list changes.
    useEffect(() => {
      setSelectedIndex(0)
    }, [providers, view, isSearching])

    const goBackToProviders = useCallback(() => {
      setView('providers')
      setSelectedProvider(null)
      setSelectedIndex(0)
    }, [])

    const drillIntoProvider = useCallback((group: ProviderGroup) => {
      // Allow drilling into not-yet-connected providers — authoring a
      // runbook against an integration the workspace will connect
      // later is a valid first-class flow. The "not connected" badge
      // stays visible so the author can see what they're picking, and
      // the executor gates at run time.
      setSelectedProvider({ id: group.provider, label: group.providerLabel })
      setView('actions')
      setSelectedIndex(0)
    }, [])

    const selectAction = useCallback(
      (index: number) => {
        const item = flatActions[index]
        if (item) props.command(item)
      },
      [flatActions, props.command]
    )

    const showProviderGrid = !isSearching && view === 'providers'
    const navTargetCount = showProviderGrid
      ? providers.length
      : flatActions.length

    React.useImperativeHandle(ref, () => ({
      onKeyDown: ({ event }: { event: React.KeyboardEvent }) => {
        if (navTargetCount === 0) return false

        if (event.key === 'ArrowDown') {
          setSelectedIndex((i) => (i + 1) % navTargetCount)
          return true
        }
        if (event.key === 'ArrowUp') {
          setSelectedIndex((i) => (i - 1 + navTargetCount) % navTargetCount)
          return true
        }
        if (event.key === 'Enter') {
          if (showProviderGrid) {
            const group = providers[selectedIndex]
            if (group) drillIntoProvider(group)
            return true
          }
          selectAction(selectedIndex)
          return true
        }
        if (
          event.key === 'Backspace' &&
          !isSearching &&
          view === 'actions' &&
          selectedProvider
        ) {
          // Backspace with no typed query while drilled into a provider
          // returns to the provider grid rather than dismissing the popup.
          goBackToProviders()
          return true
        }
        return false
      },
    }))

    // Empty catalog — only short-circuit when there's no drill-down to
    // preserve. While drilled in, fall through to the actions view so the
    // back-chevron header stays reachable.
    if (providers.length === 0 && !selectedProvider) {
      return (
        <div className='rounded-sm border border-border bg-card px-3 py-2 text-[11px] text-text-subtle'>
          No actions found
        </div>
      )
    }

    if (showProviderGrid) {
      const totalProviders = categorized.reduce(
        (n, b) => n + b.groups.length,
        0,
      )
      return (
        <div className='w-72 overflow-hidden rounded-sm border border-border bg-card shadow-md'>
          <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-3 py-2 text-[12px] text-text-subtle'>
            <Search className='h-3.5 w-3.5 text-text-placeholder' aria-hidden />
            <span className='flex-1 truncate'>
              {query ? (
                <span className='text-text-secondary'>{query}</span>
              ) : (
                <span className='text-text-placeholder'>Type to search providers and actions…</span>
              )}
            </span>
          </div>
          <ScrollArea className='max-h-72' viewClassName='max-h-72' ref={scrollRef}>
            <div className='p-1'>
              {categorized.map(({ category, groups, startIndex }) => (
                <div key={category}>
                  <div className='px-2.5 pt-2 pb-1 text-[10px] font-medium uppercase tracking-wider text-text-placeholder'>
                    {category}
                  </div>
                  {groups.map((group, i) => {
                    const idx = startIndex + i
                    const isSelected = idx === selectedIndex
                    return (
                      <button
                        key={group.provider}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 rounded-sm px-2.5 py-2 text-left transition-colors',
                          isSelected ? 'bg-selected' : 'hover:bg-surface-subtle',
                          !group.connected && 'opacity-70'
                        )}
                        onMouseEnter={() => setSelectedIndex(idx)}
                        onClick={() => drillIntoProvider(group)}
                      >
                        <div className='flex min-w-0 flex-col gap-0.5'>
                          <span className='text-[12px] font-medium text-text-secondary'>
                            {group.providerLabel}
                          </span>
                          <span className='text-[10px] text-text-subtle'>
                            {group.actions.length} action
                            {group.actions.length !== 1 ? 's' : ''}
                            {!group.connected ? ' · not connected' : ''}
                          </span>
                        </div>
                        <ChevronRight
                          className={cn(
                            'h-3.5 w-3.5 shrink-0 text-text-placeholder',
                            !group.connected && 'opacity-50'
                          )}
                          aria-hidden
                        />
                      </button>
                    )
                  })}
                </div>
              ))}
              {totalProviders === 0 && (
                <div className='px-3 py-4 text-center text-[11px] text-text-placeholder'>
                  No providers match your search.
                </div>
              )}
            </div>
          </ScrollArea>
        </div>
      )
    }

    // Actions view. When drilled into a provider, scope to that provider
    // even while searching. The unpicked + searching path still shows
    // a flat list grouped by provider.
    const groupsToRender = selectedProvider
      ? selectedProviderActions.length > 0
        ? [
            {
              provider: selectedProvider.id,
              providerLabel: selectedProvider.label,
              connected: true,
              actions: selectedProviderActions,
            },
          ]
        : []
      : providers.filter((group) => group.actions.length > 0)

    let flatIndex = 0

    return (
      <div className='w-72 overflow-hidden rounded-sm border border-border bg-card shadow-md'>
        {selectedProvider ? (
          <button
            type='button'
            className='flex w-full items-center gap-1.5 border-b border-border bg-surface-subtle px-2.5 py-1.5 text-left text-[11px] font-medium text-text-body hover:bg-muted'
            onClick={goBackToProviders}
          >
            <ChevronLeft className='h-3.5 w-3.5 text-text-subtle' aria-hidden />
            <span>{selectedProvider.label}</span>
            {isSearching ? (
              <span className='ml-auto text-[10px] text-text-placeholder'>
                {selectedProviderActions.length} match
                {selectedProviderActions.length !== 1 ? 'es' : ''}
              </span>
            ) : (
              <span className='ml-auto text-[10px] text-text-placeholder'>
                {selectedProviderActions.length} action
                {selectedProviderActions.length !== 1 ? 's' : ''}
              </span>
            )}
          </button>
        ) : (
          <div className='flex items-center gap-2 border-b border-border bg-surface-subtle px-3 py-2 text-[12px] text-text-subtle'>
            <Search className='h-3.5 w-3.5 text-text-placeholder' aria-hidden />
            <span className='flex-1 truncate'>
              {query ? (
                <span className='text-text-secondary'>{query}</span>
              ) : (
                <span className='text-text-placeholder'>Type to search providers and actions…</span>
              )}
            </span>
          </div>
        )}
        <ScrollArea className='max-h-72' viewClassName='max-h-72' ref={scrollRef}>
          {flatActions.length === 0 ? (
            <div className='px-3 py-2 text-[11px] text-text-subtle'>
              No actions match your search.
            </div>
          ) : (
            groupsToRender.map((group) => (
              <div key={group.provider}>
                {(isSearching || groupsToRender.length > 1) && (
                  <div
                    className={cn(
                      'flex items-center gap-1.5 px-3 py-1.5 text-[10px] font-medium uppercase tracking-wider',
                      group.connected ? 'text-text-body' : 'text-text-placeholder'
                    )}
                  >
                    {group.providerLabel}
                    {!group.connected && (
                      <span className='rounded-md border border-border px-1.5 py-0.5 text-[9px] font-normal normal-case text-text-placeholder'>
                        not connected
                      </span>
                    )}
                  </div>
                )}
                {group.actions.map((action) => {
                  const currentIndex = flatIndex++
                  const isSelected = currentIndex === selectedIndex
                  return (
                    <button
                      key={action.tool ?? action.checkId ?? `${action.provider}:${action.action}`}
                      className={cn(
                        'flex w-full flex-col gap-0.5 px-3 py-1.5 text-left transition-colors',
                        isSelected ? 'bg-selected' : 'hover:bg-surface-subtle',
                        !group.connected && 'opacity-50'
                      )}
                      onMouseEnter={() => setSelectedIndex(currentIndex)}
                      onClick={() => selectAction(currentIndex)}
                    >
                      <span className='text-[12px] font-medium text-text-secondary'>
                        {action.name}
                      </span>
                      {action.description && (
                        <span className='line-clamp-1 text-[10px] text-text-subtle'>
                          {action.description}
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
            ))
          )}
        </ScrollArea>
      </div>
    )
  }
)

ActionPillMenuList.displayName = 'ActionPillMenuList'
